import { IESDoc } from "../../../app.interface";
import axiosInstance from "../../../middlewares/axiosInstance";
import { AxiosResponse } from "axios";
import {
  CreateTableData,
  ListTablesByBranchAndStoreRequest,
  PosTable,
  UpdateTableData,
} from "../types/pos.types";

interface ListParams {
  searchQuery?: string;
  filters?: any[];
  sortField?: string;
  sortOrder?: "asc" | "desc";
  pageIndex?: number;
  pageSize?: number;
}

export interface ListResponse {
  data: any[];
  metadata: {
    total: {
      value: number;
    };
    took: number;
    timed_out: boolean;
    max_score: number | null;
  };
}

export interface Branch {
  id?: string;
  name: string;
  address?: string;
}

export interface WarehouseContact {
  id?: string;
  name: string;
  email: string;
  phone: string;
  whatsapp?: string;
}

export interface Warehouse {
  _id: string;
  name: string;
  address: string;
  contacts: WarehouseContact[];
  is_active: boolean;
}

export interface CreateWarehouseData {
  name: string;
  address: string;
  contacts: WarehouseContact[];
}

export interface UpdateWarehouseData extends CreateWarehouseData {
  _id: string;
}

export interface DepartmentContact {
  id?: string;
  name: string;
  email: string;
  phone: string;
  whatsapp?: string;
}

export interface Department {
  _id: string;
  name: string;
  address?: string;
  contacts?: DepartmentContact[];
  is_active: boolean;
}

export interface CreateDepartmentData {
  name: string;
  address?: string;
  contacts?: DepartmentContact[];
  is_active: boolean;
}

export interface UpdateDepartmentData extends CreateDepartmentData {
  _id: string;
}

class FacilitiesService {
  // Branches
  async listBranches(params: ListParams): Promise<ListResponse> {
    const response = await axiosInstance.post("/business-admin/branches/list", {
      ...params,
    });
    return response.data.data;
  }

  async getBranch(id: string): Promise<AxiosResponse<any>> {
    return axiosInstance.post("/business-admin/branches/get", { _id: id });
  }

  async createBranch(data: IESDoc): Promise<AxiosResponse<any>> {
    const formattedData = {
      name: data.name,
      address: data.address,
      branch_code: data.branch_code,
    };
    return axiosInstance.post("/business-admin/branches/create", formattedData);
  }

  async updateBranch(data: IESDoc): Promise<AxiosResponse<any>> {
    const formattedData = {
      _id: data._id,
      name: data.name,
      address: data.address,
      branch_code: data.branch_code,
    };
    return axiosInstance.post("/business-admin/branches/update", formattedData);
  }

  async deleteBranch(id: string): Promise<AxiosResponse<any>> {
    return axiosInstance.post("/business-admin/branches/delete", { _id: id });
  }

  // Warehouses
  async listWarehouses(params: ListParams): Promise<any> {
    const response = await axiosInstance.post(
      "/business-admin/warehouses/list",
      {
        ...params,
      }
    );
    return response.data.data;
  }

  async getWarehouse(id: string): Promise<AxiosResponse<any>> {
    return axiosInstance.post("/business-admin/warehouses/get", { id });
  }

  async createWarehouse(
    data: CreateWarehouseData
  ): Promise<AxiosResponse<any>> {
    const formattedData = {
      name: data.name,
      address: data.address,
      contacts: data.contacts,
    };
    return axiosInstance.post(
      "/business-admin/warehouses/create",
      formattedData
    );
  }

  async updateWarehouse(
    data: UpdateWarehouseData
  ): Promise<AxiosResponse<any>> {
    const formattedData = {
      _id: data._id,
      name: data.name,
      address: data.address,
      contacts: data.contacts,
    };
    return axiosInstance.post(
      "/business-admin/warehouses/update",
      formattedData
    );
  }

  async deleteWarehouse(id: string): Promise<AxiosResponse<any>> {
    return axiosInstance.post("/business-admin/warehouses/delete", { _id: id });
  }

  // Departments
  async listDepartments(params: ListParams): Promise<any> {
    const response = await axiosInstance.post(
      "/business-admin/departments/list",
      {
        ...params,
      }
    );
    return response.data.data;
  }

  async getDepartment(id: string): Promise<AxiosResponse<any>> {
    return axiosInstance.post("/business-admin/departments/get", { _id: id });
  }

  async createDepartment(
    data: CreateDepartmentData
  ): Promise<AxiosResponse<any>> {
    const formattedData = {
      name: data.name,
      address: data.address,
      contacts: data.contacts,
    };
    return axiosInstance.post(
      "/business-admin/departments/create",
      formattedData
    );
  }

  async updateDepartment(
    data: UpdateDepartmentData
  ): Promise<AxiosResponse<any>> {
    const formattedData = {
      _id: data._id,
      name: data.name,
      address: data.address,
      contacts: data.contacts,
    };
    return axiosInstance.post(
      "/business-admin/departments/update",
      formattedData
    );
  }

  async deleteDepartment(id: string): Promise<AxiosResponse<any>> {
    return axiosInstance.post("/business-admin/departments/delete", {
      _id: id,
    });
  }

  async listTablesbyBranch(branchID: string): Promise<AxiosResponse<any>> {
    return axiosInstance.post("/list-tables");
  }

  /**
   * Fetch all tables for a given branch
   */
  async listTablesByBranch(branchId: string): Promise<PosTable[]> {
    const response = await axiosInstance.post("/business-admin/tables/list", {
      branch_id: branchId,
      page: 1,
      limit: 100,
    });

    return response.data.data;
  }

  /**
   * Get a single table by ID
   */
  async getTableById(tableId: string): Promise<PosTable> {
    const response = await axiosInstance.post("/business-admin/tables/get", {
      table_id: tableId,
    });

    return response.data.data;
  }

  /**
   * Create a new table
   */
  async createTable(data: CreateTableData): Promise<PosTable> {
    const response = await axiosInstance.post("/business-admin/tables/create", {
      ...data,
      is_active: true,
      is_deleted: false,
      created_at: new Date().toISOString(),
      updated_at: new Date().toISOString(),
    });

    return response.data.data;
  }

  async getTablesByBranchAndStore(
    data: ListTablesByBranchAndStoreRequest
  ): Promise<PosTable[]> {
    const response = await axiosInstance.post(
      "/business-admin/tables/by-branch-store",
      data
    );

    return response.data.data;
  }

  async updateTable(data: UpdateTableData): Promise<PosTable> {
    const response = await axiosInstance.post(
      "/business-admin/tables/update",
      data
    );
    return response.data.data;
  }

  async deleteTable(tableId: string): Promise<PosTable> {
    const response = await axiosInstance.post("/business-admin/tables/delete", {
      table_id: tableId,
    });

    return response.data.data;
  }
}

export const facilitiesService = new FacilitiesService();
